import { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useAdminEffectiveFeatures, type EffectiveFeatureRow } from '@/hooks/useAdminEffectiveFeatures';
import { useAdminProducerPlan } from '@/hooks/useAdminProducerPlan';
import { Sparkles, Loader2, Info, X, RotateCcw, Shield } from 'lucide-react';
import { EXPLORE_DEBUG } from '@/lib/debug';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface Props {
  producerId: string | undefined;
}

const SOURCE_LABELS: Record<string, string> = {
  override: 'Admin override',
  producer: 'Producent-toggle',
  plan: 'Plan',
  missing: 'Ej konfigurerad',
};

const SOURCE_COLORS: Record<string, string> = {
  override: 'bg-primary text-primary-foreground',
  producer: 'bg-secondary text-secondary-foreground',
  plan: 'bg-muted text-muted-foreground',
  missing: 'bg-muted text-muted-foreground',
};

function FeatureRow({
  feature,
  onToggle,
  onRemoveOverride,
  onSetLimit,
  isToggling,
}: {
  feature: EffectiveFeatureRow;
  onToggle: (key: string, enabled: boolean) => void;
  onRemoveOverride: (key: string) => void;
  onSetLimit: (key: string, limit: number | null) => void;
  isToggling: boolean;
}) {
  const isOverride = feature.source_kind === 'override';
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitInput, setLimitInput] = useState(
    feature.effective_limit_value?.toString() ?? ''
  );

  const handleLimitSave = () => {
    const val = limitInput.trim() === '' ? null : parseInt(limitInput, 10);
    if (val !== null && isNaN(val)) return;
    onSetLimit(feature.feature_key, val);
    setEditingLimit(false);
  };

  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 p-4 rounded-lg border bg-card transition-colors',
        feature.effective_enabled ? 'border-primary/20 bg-primary/5' : 'border-border'
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-sm text-foreground">{feature.label_sv}</p>
          <Badge
            variant="secondary"
            className={cn('text-[10px] px-1.5 py-0', SOURCE_COLORS[feature.source_kind])}
          >
            {SOURCE_LABELS[feature.source_kind] ?? feature.source_kind}
          </Badge>
          {feature.effective_limit_value != null && (
            <Badge
              variant="outline"
              className="text-[10px] px-1.5 py-0 cursor-pointer hover:bg-muted"
              onClick={() => setEditingLimit(!editingLimit)}
            >
              max {feature.effective_limit_value}
            </Badge>
          )}
          {feature.effective_limit_value == null && feature.source_kind !== 'missing' && (
            <button
              onClick={() => setEditingLimit(!editingLimit)}
              className="text-[10px] text-muted-foreground hover:text-foreground underline"
            >
              + limit
            </button>
          )}
        </div>
        {feature.description_sv && (
          <p className="text-xs text-muted-foreground mt-0.5">{feature.description_sv}</p>
        )}
        <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
          <Info className="h-3 w-3 shrink-0" />
          {feature.reason_sv}
        </p>

        {/* Inline limit editor */}
        {editingLimit && (
          <div className="flex items-center gap-2 mt-2">
            <Input
              type="number"
              min={0}
              placeholder="Limit (tom = ingen)"
              value={limitInput}
              onChange={(e) => setLimitInput(e.target.value)}
              className="h-7 w-24 text-xs"
            />
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleLimitSave}>
              Spara
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingLimit(false)}>
              Avbryt
            </Button>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 pt-0.5 shrink-0">
        {isOverride && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => onRemoveOverride(feature.feature_key)}
              >
                <RotateCcw className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Återställ till plan (ta bort override)</TooltipContent>
          </Tooltip>
        )}
        {isToggling && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <Switch
          checked={feature.effective_enabled}
          onCheckedChange={(checked) => onToggle(feature.feature_key, checked)}
          disabled={isToggling}
        />
      </div>
    </div>
  );
}

function PlanSelector({ producerId }: { producerId: string | undefined }) {
  const { subscription, plans, isLoading, changePlan, isChanging } = useAdminProducerPlan(producerId);

  if (isLoading) return <Skeleton className="h-10 w-48" />;

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm font-medium text-foreground">Plan:</span>
      <Select
        value={subscription?.plan_id ?? 'free'}
        onValueChange={(val) => changePlan(val)}
        disabled={isChanging}
      >
        <SelectTrigger className="w-40 h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {plans.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {p.name_sv}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isChanging && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
    </div>
  );
}

export function AdminEffectiveFeaturesPanel({ producerId }: Props) {
  const {
    features,
    isLoading,
    toggleFeature,
    setLimitOverride,
    removeOverride,
    isToggling,
  } = useAdminEffectiveFeatures(producerId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (features.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>Inga features tillgängliga.</p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4" />
          Åtkomst & funktioner
        </CardTitle>
        <CardDescription>
          Effektiv åtkomst per feature. Toggles skapar admin-overrides.
        </CardDescription>
        <div className="pt-2">
          <PlanSelector producerId={producerId} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {features.map((f) => (
          <FeatureRow
            key={f.feature_key}
            feature={f}
            onToggle={toggleFeature}
            onRemoveOverride={removeOverride}
            onSetLimit={setLimitOverride}
            isToggling={isToggling === f.feature_key}
          />
        ))}

        {EXPLORE_DEBUG && (
          <details className="mt-4 text-xs text-muted-foreground">
            <summary className="cursor-pointer hover:text-foreground">
              🐛 Debug: raw gating inputs
            </summary>
            <pre className="mt-2 p-3 bg-muted rounded text-[11px] overflow-auto max-h-80">
              {JSON.stringify(features, null, 2)}
            </pre>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
